import i18, { t } from "i18next";
import { makeAutoObservable } from "mobx";
import { ReactNode } from "react";
import enRes from "src/lang/en";
import ruRes from "src/lang/ru";
import { ZUser } from "src/types/ZUser";

import { getCurrentUser, login, logout } from "./common/api/apiAuth";
import { detectLang } from "./common/detectLang";
import { onError } from "./common/onError";
import { RemoteData } from "./common/RemoteData";
import { PageUrl } from "./routes/PageUrl";

export type OverlayPage = "login";

export type BreadcrumbItem = {
  title: ReactNode;
  page?: PageUrl;
  params?: Record<string, string>;
};

type PageTitle = BreadcrumbItem[] | ReactNode;

export const appStore = makeAutoObservable({
  userData: {} as RemoteData<ZUser>,
  setUserData(data: RemoteData<ZUser>) {
    this.userData = data;
  },
  get userInfo(): ZUser {
    if (this.userData.status !== "ready") {
      throw Error("Empty user data");
    }
    return this.userData.result;
  },
  get userInfoSafe(): ZUser | undefined {
    const { userData } = this;
    return userData.status === "ready" ? userData.result : undefined;
  },

  overlayType: null as OverlayPage | null,
  setOverlayType(newType: OverlayPage | null) {
    this.overlayType = newType;
  },

  async init() {
    this.setUserData({ status: "wait" });
    try {
      // Пытаемся загрузить информацию о пользователе с существующими куками
      // Если куки присутствуют и валидны, это будет успешно
      this.setUserData({ status: "ready", result: await getCurrentUser() });
    } catch (error) {
      // Если загрузка информации о пользователе не удалась (нет куков или истекли), показываем страницу входа
      this.setOverlayType("login");
      this.setUserData({ status: "none" });
      const finalError =
        error.status === 401 ? new Error(t("Authorization required")) : error;
      onError(finalError);
    }
  },

  async login(email: string, password: string) {
    this.setUserData({ status: "wait" });

    try {
      // Вызываем API для получения токенов
      await login({ email, password });
      // После успешной авторизации загружаем информацию о пользователе
      const userInfo = await getCurrentUser();

      this.setUserData({
        status: "ready",
        result: userInfo,
      });
      this.setOverlayType(null);
    } catch (e) {
      onError(e);
      this.setUserData({ status: "error", error: e });
    }
  },

  async logout() {
    // Можно не ждать ответа, т.к. куки будут удалены сервером
    logout().catch(onError);
    this.setOverlayType("login");
    this.setUserData({ status: "none" });
  },

  pageTitle: "" as PageTitle,
  setPageTitle(title: PageTitle) {
    this.pageTitle = title;
  },

  get isMetadataEditor(): boolean | undefined {
    // Сейчас Редактор метаданных - это свойство пользователя.
    // Но это похоже на костыль (на уровне проектирования).
    // Возможно, он будет перенесён в роль.
    // Поэтому добавлена абстракция в виде isMetadataEditor, чтобы в случае изменения реализации не ломать код.
    return this.userInfoSafe?.metadataEditor;
  },
});

const curLang = detectLang();

i18.init({
  lng: curLang,
  debug: false,
  interpolation: {
    escape: (x: string) => x,
  },
  resources: {
    en: enRes,
    ru: ruRes,
  },
});
